/* eslint-disable react/no-unstable-nested-components */
import { twClassMerge, useMeasureElementHeight } from '@healthhub/react-ui';
import { Collapse, Divider, Typography } from 'antd';

import { TelegramIcon, TikTopIcon } from '@shared/assets';
import { OutlinedDownArrowIcon, PortalOutlinedMailCheckIcon } from '@shared/assets-icon';
import { Button, SmartLink } from '@shared/components';
import useToggle from '@shared/hooks/useToggle/useToggle';
import { HEALTHHUB_TELEGRAM_URL, HEALTHHUB_TIKTOK_URL } from '@shared/utils/api/configs/URL';

import { NewsLetterModal } from './NewsLetterModal';
import { useHeaderMenu } from '../hooks';

const { Text } = Typography;

interface MobileHeaderDrawerProps {
  isOpen: boolean;
}

const PAGE_HEADER_HEIGHT = 56;

const MobileHeaderDrawer = ({ isOpen }: MobileHeaderDrawerProps) => {
  const { headerMenu, mobileMenu } = useHeaderMenu();
  const [isNewsLetterModalOpen, toggleNewsLetterModalOpen] = useToggle(false);
  const targetElement = document.getElementById('Notices');
  const noticesHeight = useMeasureElementHeight(targetElement || document.createElement('div'));

  return (
    <div
      className={twClassMerge(
        'absolute lg:hidden w-0 left-0 right-0 top-full z-drawer translate-x-full transition-transform duration-300 flex flex-col overflow-y-auto overflow-x-hidden',
        isOpen && 'translate-x-0 w-screen'
      )}
      style={{
        height: `calc(100dvh - ${PAGE_HEADER_HEIGHT + noticesHeight}px)`,
      }}
    >
      <div className="flex flex-1 flex-col bg-primary-green-4">
        <Collapse
          accordion
          className="w-full border-none leading-none [&>.ant-collapse-item:last-child]:border-b [&>.ant-collapse-item:last-child]:border-solid  [&_.ant-collapse-expand-icon]:h-[25px] [&_.ant-collapse-expand-icon]:ps-0"
          expandIcon={({ isActive }) => (
            <OutlinedDownArrowIcon
              className={twClassMerge(
                '[&>path]:stroke-white [&>path]:stroke-2 transform duration-300 h-5 w-5 static text-3',
                isActive && 'rotate-180'
              )}
            />
          )}
          expandIconPosition="end"
          items={headerMenu.map(({ component, label }) => ({
            key: label,
            label: <span className="text-base text-white">{label}</span>,
            children: component,
            className:
              'w-full border-white bg-primary-green-4 [&_.ant-collapse-content-box]:p-0 [&_.ant-collapse-header]:flex  [&_.ant-collapse-header]:items-center [&_.ant-collapse-header]:px-4 ',
          }))}
        />
        {mobileMenu.map(({ label, url }) => (
          <SmartLink
            className="h-12 w-full content-center border-b border-white px-4 text-left text-base text-white"
            key={label}
            to={url}
          >
            {label}
          </SmartLink>
        ))}
      </div>
      <div className="flex flex-col gap-6 bg-functional-haze px-3 py-8">
        <div className="flex flex-col gap-4">
          <div className="flex gap-2 self-center">
            <PortalOutlinedMailCheckIcon className="size-4" />
            <div className="text-sm font-semibold text-primary-blue-grey">
              Stay up to date with the latest news on health.
            </div>
          </div>
          <Button className="bg-white" onClick={toggleNewsLetterModalOpen} variant="secondary">
            Sign up now
          </Button>
          <NewsLetterModal isOpen={isNewsLetterModalOpen} onClose={toggleNewsLetterModalOpen} />
        </div>
        <Divider className="m-0 border-dashed border-primary-green-3" />
        <div className="flex flex-col items-center gap-2">
          <Text className="text-sm">Follow us:</Text>
          <div className="flex gap-5">
            <a href={HEALTHHUB_TELEGRAM_URL} rel="noopener noreferrer" target="_blank">
              <img alt="Telegram" src={TelegramIcon} />
            </a>
            <a href={HEALTHHUB_TIKTOK_URL} rel="noopener noreferrer" target="_blank">
              <img alt="TikTok" src={TikTopIcon} />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileHeaderDrawer;
